import { inject, Injectable } from '@angular/core';
import {
  finalize,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subscription,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { ApiService } from '~/shared-core/http-client';
import { AuthStore, LocalStorageJwtService } from '../stores/auth';

@Injectable({ providedIn: 'root' })
export class StylerAuthService {
  private readonly apiService = inject(ApiService);
  private readonly authStore = inject(AuthStore);
  private readonly localStorageJwtService = inject(LocalStorageJwtService);
  private readonly tokenSubject = new ReplaySubject<string>(1);
  private readonly cache = new Map<
    string,
    { token: string; expiresAt: number }
  >();
  private readonly pending = new Map<string, Observable<string>>();
  private refreshSubscription?: Subscription;

  readonly token$ = this.tokenSubject.asObservable();

  getJtCompanyId(jtCompanyId = ''): string {
    return String(
      jtCompanyId ||
        this.localStorageJwtService.getItemSTR('user_jt_company_id') ||
        this.localStorageJwtService.getItemSTR('jt_company_id') ||
        this.authStore.user()?.company?.jt_company_id ||
        '',
    ).trim();
  }

  getToken(jtCompanyId = '', forceRefresh = false): Observable<string> {
    const selectedJtCompanyId = this.getJtCompanyId(jtCompanyId);

    if (!selectedJtCompanyId) {
      return throwError(() => new Error('Styler company ID is not available'));
    }

    const cached = this.cache.get(selectedJtCompanyId);
    if (!forceRefresh && cached && cached.expiresAt - Date.now() > 60_000) {
      return of(cached.token);
    }

    const pending = this.pending.get(selectedJtCompanyId);
    if (pending) return pending;

    const query = `
      query StylerCustomerToken($jtCompanyId: String!) {
        stylerCustomerToken(jtCompanyId: $jtCompanyId)
      }
    `;
    const request = this.apiService
      .query<{ stylerCustomerToken: string }>(query, {
        jtCompanyId: selectedJtCompanyId,
      })
      .pipe(
        map((result) => result.stylerCustomerToken),
        tap((token) => {
          const expiresAt = this.getTokenExpiresAt(token);
          this.cache.set(selectedJtCompanyId, { token, expiresAt });
          this.tokenSubject.next(token);
          this.scheduleRefresh(selectedJtCompanyId, expiresAt);
        }),
        finalize(() => this.pending.delete(selectedJtCompanyId)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.pending.set(selectedJtCompanyId, request);
    return request;
  }

  private scheduleRefresh(jtCompanyId: string, expiresAt: number): void {
    this.refreshSubscription?.unsubscribe();
    const refreshIn = Math.max(expiresAt - Date.now() - 5 * 60_000, 0);
    this.refreshSubscription = timer(refreshIn)
      .pipe(switchMap(() => this.getToken(jtCompanyId, true)))
      .subscribe({ error: () => undefined });
  }

  private getTokenExpiresAt(token: string): number {
    try {
      const encodedPayload = token.split('.')[1];
      const normalized = encodedPayload
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
      const payload = JSON.parse(globalThis.atob(normalized)) as {
        exp?: number;
      };
      if (payload.exp) return payload.exp * 1000;
    } catch {
      // Server issues Styler tokens for 8 hours; use that TTL as fallback.
    }
    return Date.now() + 8 * 60 * 60_000;
  }
}
